/**
 * The questionnaire page's helpers (15.10.2026). The questions themselves come
 * from the server (`services/assessment/questionnaire.py`), so the page and
 * the check on submit can never disagree about them. The words around them
 * were validated in docs/texte-evaluare-initiala-2026-10-15.md.
 */

export const TEXT = {
  note: (group) => `Nu e un test și nu primești o notă. Răspunsurile tale le văd doar echipa FPED și formatorul grupei ${group}.`,
  prefilled: "Am completat din înscrierea ta. Corectează dacă e cazul.",
  saved: "Răspunsurile tale se păstrează în acest browser până le trimiți, așa că poți reveni dacă închizi pagina.",
  answerThis: "Răspunde la această întrebare.",
  network: "Nu am putut trimite răspunsurile. Verifică conexiunea și apasă din nou Trimite. Răspunsurile tale sunt încă aici.",
  started: (day) => `Cursul a început deja, dar poți completa chestionarul până pe ${day}. Formatorul îl citește și acum.`,
  reopened: (day) => `Ți-am redeschis chestionarul. Îl poți completa până pe ${day}.`,
};

/** The token after `#t=` in the address; never sent to the server in a URL. */
export function tokenFromHash(hash = "") {
  const m = /[#&]t=([A-Za-z0-9_-]{10,200})/.exec(hash || "");
  return m ? m[1] : "";
}

const RO = "Europe/Bucharest";

/** "joi, 5 noiembrie", by Romania's clock. */
export function dayLabel(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("ro-RO", { weekday: "long", day: "numeric", month: "long", timeZone: RO }).format(d);
}

/** "28 octombrie". */
export function shortDay(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("ro-RO", { day: "numeric", month: "long", timeZone: RO }).format(d);
}

/** "16 noiembrie, ora 10:20". */
export function dayTime(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const day = shortDay(iso);
  const time = new Intl.DateTimeFormat("ro-RO", { hour: "2-digit", minute: "2-digit", timeZone: RO }).format(d);
  return `${day}, ora ${time}`;
}

/** Tick or untick a choice of a multi-answer question; the lone one clears the rest, and the rest clear it. */
export function toggleChoice(list, key, alone) {
  const current = Array.isArray(list) ? list : [];
  if (current.includes(key)) return current.filter((k) => k !== key);
  if (alone && key === alone) return [key];
  return [...current.filter((k) => k !== alone), key];
}

/** The key of the "Altele" choice that needs a text, or "" for a question without one. */
function otherKey(q) {
  return q.other || "";
}

/** The questions still unanswered, as the server would name them. */
export function missing(definition, answers) {
  const out = [];
  const a = answers || {};
  (definition?.sections || []).forEach((section) => section.questions.forEach((q) => {
    const v = a[q.id];
    let ok;
    if (q.type === "multi") {
      ok = Array.isArray(v) && v.length > 0;
      const other = otherKey(q);
      if (ok && other && v.includes(other) && !String(a[`${q.id}_other`] || "").trim()) ok = false;
    } else if (q.type === "grid") {
      ok = q.rows.every((r) => Number(v?.[r.key]) >= q.min && Number(v?.[r.key]) <= q.max);
    } else if (q.type === "scale") {
      ok = Number(v) >= q.min && Number(v) <= q.max;
    } else {
      ok = Boolean(String(v || "").trim());
    }
    if (!ok) out.push(q.id);
  }));
  return out;
}

export function missingLine(count) {
  return count === 1 ? "Mai ai de răspuns la o întrebare. Am marcat-o mai sus."
    : `Mai ai de răspuns la ${count} întrebări. Le-am marcat mai sus.`;
}

// ------------------------------------------------------------------ the draft in the browser

const DRAFT_PREFIX = "fped_assessment_draft:";

export function loadDraft(key, storage = typeof window !== "undefined" ? window.localStorage : null) {
  if (!key || !storage) return null;
  try {
    const raw = storage.getItem(DRAFT_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveDraft(key, answers, storage = typeof window !== "undefined" ? window.localStorage : null) {
  if (!key || !storage) return;
  try { storage.setItem(DRAFT_PREFIX + key, JSON.stringify(answers)); } catch { /* full or blocked: nothing lost on screen */ }
}

export function clearDraft(key, storage = typeof window !== "undefined" ? window.localStorage : null) {
  if (!key || !storage) return;
  try { storage.removeItem(DRAFT_PREFIX + key); } catch { /* ignore */ }
}
